<template>
  <div class="buyticket">
    <!-- チケットストア帯ヘッダー -->
    <TheStoreHeader page="1" :is0yen-ticket="is0yenTicket" />
    <section class="form">
      <!-- チケット名 -->
      <div v-if="masterBookSet && !hasSelectedFeature">
        <!-- MasterBookSetが存在する&&券種未選択の場合 -->
        <MbNameDisp :name="masterBookSet.name" />
      </div>
      <div v-else>
        <MbNameDisp :name="masterBook.name" />
      </div>
      <!-- チケット名 -->

      <!-- 券種選択UI -->
      <MbFeatureSelect
        v-if="masterBookSet"
        :features="masterBookSet.features"
        :on-selected="setFeature"
      />
      <!-- 券種選択UI -->

      <!-- MasterBookSetが存在する&&券種未選択の場合 -->
      <div v-if="masterBookSet && !hasSelectedFeature">
        <!-- キャンセルボタン -->
        <ItemButton
          :label="$t('common.cancelButtonLabel')"
          action-type="btn-cancel"
          @clicked="gotoMasterbook()"
        />
        <!-- キャンセルボタン -->
      </div>
      <div v-else>
        <!-- 利用開始日UI・注意書き -->
        <TheBbStartDateSelect
          v-if="!isNoNeedDateSelect"
          :on-selected-date="setStartDateOfUse"
          :start-date-of-use="startDateOfUse"
          :master-book="masterBook"
        />
        <!-- 利用開始日UI・注意書き -->

        <!-- 制限フラグが立っていて買えないので注釈のみ表示する -->
        <div v-if="isRestrictedMasterBook">
          <p class="error-block">※購入数の上限に達しているため購入できません</p>
        </div>

        <div v-else>
          <!-- 駅選択UI・注意書き -->
          <!-- 駅選択が必要な場合のみ表示 -->
          <TheBbStationSelect
            v-if="
              masterBook.master_prices[0].from_station &&
              masterBook.master_prices[0].from_station.code != -1
            "
            :set-station="setStation"
            :master-book="masterBook"
          />
          <!-- 駅選択UI・注意書き -->

          <!-- 購入枚数設定UI -->
          <!-- dataDivisionsに値が入ってから描画 -->
          <TheMbDivisionCount
            v-if="dataDivisions.length > 0"
            :data-divisions="dataDivisions"
            :set-status-is-radio="setStatusIsRadio"
            :change-radio="changeRadio"
          />
          <!-- 購入枚数設定UI -->
        </div>

        <!-- チケット規約 -->
        <TheMbImportantLinkCollection
          v-if="masterBook.content.terms_url"
          :url="masterBook.content.terms_url"
        />
        <!-- チケット規約 -->

        <!-- アクションボタン -->
        <TheBbActions
          :is-radio="isRadio"
          :data-divisions="dataDivisions"
          :is-restricted-master-book="isRestrictedMasterBook"
          :selected-station-code="selectedStationCode"
          :is-date-limited-in="isValidDate || isNoNeedDateSelect"
          :start-date-of-use="startDateOfUse"
        />
        <!-- アクションボタン -->
      </div>
    </section>

    <!-- 券種選択時のローディング -->
    <!-- ローディングダイアログ -->
    <DialogLoading ref="DialogLoading" :timeout-seconds="7000" />
  </div>
</template>

<script>
import { onMounted, computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";

import divisionService from "@/services/bookBookingDivision";
import dateService from "@/services/bookBookingDate";

import API from "@/API";

export default {
  name: "TemplateBookBooking",
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    // 券種選択時のローディングダイアログ
    const DialogLoading = ref(null);

    const masterBook = computed(() => store.getters["ticketing/masterBook"]);
    const masterBookSet = computed(
      () => store.getters["ticketing/masterBookSet"]
    );

    const hasSelectedFeature = ref(false);
    const selectedStationCode = ref(0);
    const startDateOfUse = ref("");
    const dataDivisions = ref([]);
    const isRadio = ref(false);
    const storeDivisions = computed(
      () => store.getters["input/ticketing/storeDivisions"]
    );

    const isValidDate = computed(() =>
      dateService.checkIsValidDate(
        masterBook.value.purchase_started_at,
        masterBook.value.purchase_limited_at,
        startDateOfUse.value
      )
    );
    const isNoNeedDateSelect = computed(() =>
      dateService.checkIsNoNeedDateSelect(masterBook.value)
    );
    const isPriceCalendars = computed(() =>
      dateService.checkIsPriceCalendar(masterBook.value)
    );
    // group_gettable_countsの数で購入制限を立てるフラグ
    const isRestrictedMasterBook = ref(false);
    const isReadyToDraw = ref(false);

    const is0yenTicket = route.query.is_0yen_ticket == "true" ? true : false;

    // 日付選択時の値セット
    const setStartDateOfUse = (selectedDate) => {
      startDateOfUse.value = selectedDate;
      // 料金カレンダーの場合は日付ごとに価格が異なるため、日付選択時に実行する。
      if (isPriceCalendars.value) {
        const previousDivisions =
          dataDivisions.value.length > 0 ? dataDivisions.value : null;
        // storeのdivisionsを初期化
        store.commit("input/ticketing/setStoreDivisions", null);
        initBookBooking(previousDivisions);
      }
    };

    // 駅選択時の値セット
    const setStation = (station) => {
      selectedStationCode.value = station;
    };

    // 現地用の駅コードセット
    const setStationCodeForLocal = () => {
      // 現地券は初期値を-1に設定
      if (
        masterBook.value.master_prices[0].from_station &&
        masterBook.value.master_prices[0].from_station.code == -1
      ) {
        selectedStationCode.value = -1;
      }
    };

    // 券種選択時の値セット
    const setFeature = async (masterBookId) => {
      // masterBook情報の取得に時間がかかるのでローディングを表示
      DialogLoading.value.openDialog();
      hasSelectedFeature.value = false;

      // storeのdivisionsを初期化
      store.commit("input/ticketing/setStoreDivisions", null);

      dataDivisions.value = [];
      startDateOfUse.value = "";
      if (masterBookId == 0) {
        // 初期値(券種を選択してください)を選んだ場合は、masterBookの選択をリセット
        store.commit("ticketing/setMasterBook", []);
      } else {
        // GET /ticket-detail でMasterBook情報取得
        await API.getMasterBook(masterBookId);
        // 料金カレンダーの場合は券種選択時には料金が確定していないので実行しない。
        if (!isPriceCalendars.value) {
          initBookBooking();
        }
        hasSelectedFeature.value = true;
        // Reset selected station code
        if (masterBook.value.master_prices[0].from_station == null) {
          selectedStationCode.value = 0;
        }
      }

      DialogLoading.value.closeDialog();
    };

    const checkGroupLimit = () => {
      // gettable_countsより上位にあたる制限で、group_limited_typeが"master_book"かつ残数が0の場合は
      // gettable_countsの残数に関わらず、購入が不可能となります
      if (
        masterBook.value._max_purchase_count[0].group_limited_type ==
          "master_book" &&
        masterBook.value._max_purchase_count[0].group_gettable_counts[0]
          .count == 0
      ) {
        isRestrictedMasterBook.value = true;
      }
    };

    // ラジオ選択時の値変更。子コンポーネント(TheMbDivisionCount/TheMbDivisionCountRadio)でやるとエラーになるのでここで定義
    const changeRadio = (index) => {
      // 選択したdivisionのみ、数量1をセットしてそれ以外は0に
      for (var i = 0; i < dataDivisions.value.length; i++) {
        if (i == index) {
          dataDivisions.value[i].count = 1;
        } else {
          dataDivisions.value[i].count = 0;
        }
      }
    };

    // ラジオボタンタイプの場合にフラグをセット(子コンポーネントから)
    const setStatusIsRadio = (bool) => {
      isRadio.value = bool;
    };

    const generateDataDivisions = (previousDivisions = null) => {
      dataDivisions.value = divisionService.getDataDivisions(
        storeDivisions.value,
        masterBook.value,
        startDateOfUse.value,
        previousDivisions
      );
    };

    const initBookBooking = (previousDivisions = null) => {
      generateDataDivisions(previousDivisions);
      setStationCodeForLocal();
      checkGroupLimit();
    };

    const gotoMasterbook = () => {
      router.go(-1);
      // masterBookService.goToBook(masterBook.value, router);
    };

    onMounted(() => {
      // 券種まとめなしの場合は描画時にdataDivisions生成
      if (masterBookSet.value === null) {
        initBookBooking();
      }
    });

    return {
      DialogLoading,
      setStartDateOfUse,
      setStation,
      isNoNeedDateSelect,
      hasSelectedFeature,
      isRestrictedMasterBook,
      selectedStationCode,
      setFeature,
      startDateOfUse,
      isValidDate,
      dataDivisions,
      isRadio,
      changeRadio,
      setStatusIsRadio,
      masterBook,
      masterBookSet,
      isReadyToDraw,
      is0yenTicket,
      gotoMasterbook,
    };
  },
};
</script>

<style lang="scss" scoped>
.buyticket {
  padding-bottom: 50px;
}
.form {
  margin: 20px 15px;
}

.error-block {
  margin: 5px 0 0 0;
  font-size: 13px;
  font-weight: bold;
  text-align: right;
  line-height: 17px;
  color: #ff3333;
}
</style>
